import styled from "styled-components";
import { LostStatuses, PendingRequestOperation } from "../../../../../types/LostStatuses";
import { Tag } from "antd";
import { ExclamationOutlined } from "@ant-design/icons";
import { ReactComponent as ShipDeliveredIcon } from "../../../../../assets/images/ShipDelivered.svg";
import { getStatusTheme } from "../../statusThemes";

/**
 * Traffic-light treatment for (status, isRecentlyPaid) combos.
 *
 *   Open   + in refund window → RED   (brand-new paid claim — attention)
 *   Review + in refund window → AMBER (in review, refund still possible — caution)
 *
 * Removed: "Review + out of window → GREEN". Cards in that combo now auto-move to
 * InProcess (which is permanently green-themed). Until the hourly cron has run since
 * deploy, Review may still contain past-window cards — those render the default Review
 * amber, NOT green, to keep the "green = go" signal anchored to InProcess.
 *
 * Returns null for every other combo so the default status theme + dashed/opacity
 * rules apply.
 */
const getTrafficLightTheme = (status?: number, isRecentlyPaid?: boolean): { border: string; background: string } | null => {
  if (status === LostStatuses.Open && isRecentlyPaid) {
    return { border: "#EF4444", background: "#FEE2E2" };
  }
  if (status === LostStatuses.Review && isRecentlyPaid) {
    return { border: "#F59E0B", background: "#FEF3C7" };
  }
  return null;
};

export const TicketWrapper = styled.div<any>`
  /* In refund window (isRecentlyPaid): dashed border so admins still see the "refund could come" signal.
     Out of window: solid border. The traffic-light combos only override the border COLOR + bg color. */
  border: 1px ${({ isRecentlyPaid }) => (isRecentlyPaid ? "dashed" : "solid")} ${({ status, isRecentlyPaid, isUrgent }) => {
    if (isUrgent) return getStatusTheme(LostStatuses.Urgent).border;
    const tl = getTrafficLightTheme(status, isRecentlyPaid);
    return tl ? tl.border : getStatusTheme(status).border;
  }};
  border-radius: 8px;
  padding: 12px;
  background-color: ${({ status, isUrgent, isPendingRequest, requestType, isRecentlyPaid }) => {
    if (isPendingRequest && (requestType === PendingRequestOperation.Delete || requestType === PendingRequestOperation.Update || requestType === PendingRequestOperation.UpdateShippingInfo)) {
      return "#fce8e8";
    }
    if (isUrgent && !isPendingRequest) {
      return getStatusTheme(LostStatuses.Urgent).background;
    }
    const tl = getTrafficLightTheme(status, isRecentlyPaid);
    if (tl) return tl.background;
    const theme = getStatusTheme(status);
    return theme.background;
  }};
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 10px 0px;
  width: 100%;
  box-sizing: border-box;
  /* In refund window: muted "skip for now" opacity (unchanged). Out of window: full opacity. */
  opacity: ${({ isRecentlyPaid }) => (isRecentlyPaid ? 0.55 : 1)};
  transition: box-shadow 0.2s ease, opacity 0.2s ease;

  &:hover {
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.15);
    opacity: 1;
  }
`;

export const ReportDateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .icon {
    font-size: 14px;
    color: #b3b3b3;
    cursor: pointer;

    &:hover {
      color: #595959;
    }
  }
`;

export const HeaderLeftContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  > svg {
    height: 16px;
    width: 16px;
    flex-shrink: 0;
  }
`;

export const HeaderRightContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  svg {
    height: 20px;
    width: 20px;
  }
`;

export const Title = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #000;
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  line-height: 1.35;
`;

export const InfoText = styled.div`
  font-weight: 500;
  font-size: 12px;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

export const InfoTextContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
  flex: 1;
  width: 100%;
`;

export const SubTitle = styled.div`
  font-size: 12px;
  color: #000;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  /* margin-bottom: 12px; */
`;

export const Info = styled.div`
  font-size: 12px;
  color: #b3b8c1;
`;

export const DaysPast = styled.div<{ isOverdue: boolean; isOnTime: boolean }>`
  font-size: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  font-weight: bold;
  padding: 2px 4px;
  border-radius: 100px;
  font-weight: 500;
  color: #000;
`;

export const ReportDateMainContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const UpdateAndDeleteReportLabel = styled.div<{ bgColor?: string }>`
  background-color: ${({ bgColor }) => bgColor};
  color: #fff;
  font-size: 12px;
  border-radius: 100%;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  width: 20px;

  svg {
    height: 16px !important;
    width: 16px !important;
  }
`;

export const ReportDateContainer = styled.div``;

export const ShipWrapper = styled.div`
  font-size: 14px;
  color: #000;
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const StyledSubTitle = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 500 !important;
`;

export const StyledSpan = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
`;

export const StyledMoreTitle = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  color: #595959;
  font-weight: 500 !important;
  white-space: nowrap;
  background-color: #f0f0f0;
  border-radius: 10px;
  line-height: 1.2;
`;

export const StatusPill = styled.span<{ $textColor?: string; $backgroundColor?: string }>`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.3;
  color: ${({ $textColor }) => $textColor || "#1f1f1f"};
  background-color: ${({ $backgroundColor }) => $backgroundColor || "#f0f0f0"};
  pointer-events: none;
  flex-shrink: 0;
`;

export const AssigneeName = styled(Tag)`
  margin-right: 0;
  border-radius: 16px;
  padding: 0px 8px;
  font-size: 10px;
  background-color: #ffffff;
  color: #000000;
`;

export const StyledExclamationIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 12px !important;
  /* width: 20px; */
`;

export const StyledExclamationIcon = styled(ExclamationOutlined)`
  && {
    background-color: #ff0000;
    color: white;
    border-radius: 50%;
    padding: 2px;
    font-size: 10px; /* Try values like 10px, 12px */
    line-height: 1;
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const StyledShipDeliveredIcon = styled(ShipDeliveredIcon)`
  width: 18px !important;
  height: 18px !important;
`;

export const LfLocationBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #fee2e2;
  border: 1px solid #fca5a5;
  color: #991b1b;
  font-size: 10px;
  font-weight: 800;
  padding: 1px 5px;
  border-radius: 8px;
  margin-left: 2px;
  line-height: 1.4;
  white-space: nowrap;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
`;

export const RightIconsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

export const PaidAmountBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #389e0d;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  padding: 0px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
`;

export const OverdueBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #b45309;
  background-color: #fef3c7;
  border: 1px solid #fcd34d;
  padding: 0px 6px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: default;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;
